import React, { useEffect, useMemo, useState } from 'react'
import BigNumber from 'bignumber.js'
import {
  useAccount,
  useContractRead,
  usePrepareContractWrite,
  useContractWrite,
  useWaitForTransaction,
} from 'wagmi'
import { parseUnits } from 'viem'
import { MaxUint256 } from 'ethers'
import { oldToken, newToken, ipcSwapAdd, chainId } from '../../config'
import erc20Abi from '../../abi/erc20.json'
import ipcSwapAbi from '../../abi/ipcswap.json'
import { toast } from 'react-hot-toast'
import ConnectWallet from './../ConnectWallet'

const bn = (v) => new BigNumber(v || 0)
const fmt = (n) => bn(n).toFormat()

// Format numbers with commas for better readability
const formatNumber = (num) => {
  if (!num) return '0'
  const n = bn(num)
  return n.toFormat(0, BigNumber.ROUND_DOWN)
}

// Countdown Timer Component
const CountdownTimer = ({ targetTimestamp }) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    const updateTimer = () => {
      const now = Math.floor(Date.now() / 1000)
      const target = Number(targetTimestamp)
      const diff = target - now

      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 })
      } else {
        const hours = Math.floor(diff / 3600)
        const minutes = Math.floor((diff % 3600) / 60)
        const seconds = diff % 60
        setTimeLeft({ hours, minutes, seconds })
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [targetTimestamp])

  return (
    <div className="countdown-timer">
      <div className="time-segment">
        <span className="time-value">{String(timeLeft.hours).padStart(2, '0')}</span>
        <span className="time-label">Hours</span>
      </div>
      <div className="time-segment">
        <span className="time-value">{String(timeLeft.minutes).padStart(2, '0')}</span>
        <span className="time-label">Minutes</span>
      </div>
      <div className="time-segment">
        <span className="time-value">{String(timeLeft.seconds).padStart(2, '0')}</span>
        <span className="time-label">Seconds</span>
      </div>
    </div>
  )
}

// Progress Bar Component
const ProgressBar = ({ current, total, label, color = '#00f6ff' }) => {
  const percentage = total > 0 ? Math.min((current / total) * 100, 100) : 0

  return (
    <div style={{ marginBottom: 15 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: '12px', color: '#a1a1a1' }}>{label}</span>
        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
          {formatNumber(current)} / {formatNumber(total)}
        </span>
      </div>
      <div style={{
        width: '100%',
        height: '8px',
        backgroundColor: '#161b22',
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          backgroundColor: color,
          transition: 'width 0.3s ease',
          borderRadius: '4px'
        }} />
      </div>
    </div>
  )
}

const IpcSwapCard = ({ buttonColor = '#00f6ff' }) => {
  const { address } = useAccount()
  const [amount, setAmount] = useState('')
  const [displayAmount, setDisplayAmount] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Handle amount input with comma formatting
  const handleAmountChange = (value) => {
    // Remove commas and any non-numeric characters except decimal point
    const cleanValue = value.replace(/,/g, '').replace(/[^\d.]/g, '')

    // Prevent multiple decimal points
    const parts = cleanValue.split('.')
    const formattedClean = parts.length > 2
      ? parts[0] + '.' + parts.slice(1).join('')
      : cleanValue

    setAmount(formattedClean)

    // Format for display with commas
    if (formattedClean) {
      const [integerPart, decimalPart] = formattedClean.split('.')
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      setDisplayAmount(decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger)
    } else {
      setDisplayAmount('')
    }
  }

  const oldBalRead = useContractRead({
    address: oldToken,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address,
    chainId,
    watch: true,
  })

  const newBalRead = useContractRead({
    address: newToken,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address,
    chainId,
    watch: true,
  })

  const allowanceRead = useContractRead({
    address: oldToken,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [address, ipcSwapAdd],
    enabled: !!address,
    chainId,
    watch: true,
  })

  const liquidityRead = useContractRead({
    address: newToken,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [ipcSwapAdd],
    chainId,
    watch: true,
  })

  const swapRateRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'getSwapRate',
    chainId,
    watch: true,
  })

  const dailyGlobalRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'getRemainingDailyGlobalAmount',
    chainId,
    watch: true,
  })

  const dailyWalletRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'getRemainingDailyWalletAmount',
    args: [address],
    enabled: !!address,
    chainId,
    watch: true,
  })

  const totalGlobalLimitRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'dailyGlobalLimit',
    chainId,
    watch: true,
  })

  const totalWalletLimitRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'dailyWalletLimit',
    chainId,
    watch: true,
  })

  const resetPeriodRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'getResetPeriod',
    chainId,
    watch: true,
  })

  const nextResetTimeRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'getNextResetTime',
    chainId,
    watch: true,
  })

  const pausedRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'paused',
    chainId,
    watch: true,
  })

  const dailySwappedRead = useContractRead({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'dailySwappedAmount',
    chainId,
    watch: true,
  })

  const oldBal = useMemo(() => {
    const raw = oldBalRead.data ? bn(oldBalRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [oldBalRead.data])

  const newBal = useMemo(() => {
    const raw = newBalRead.data ? bn(newBalRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [newBalRead.data])

  const allowance = useMemo(
    () => (allowanceRead.data ? bn(allowanceRead.data.toString()) : bn(0)),
    [allowanceRead.data],
  )

  const allowanceHuman = useMemo(() => allowance.div(bn(10).pow(6)), [allowance])

  const liquidity = useMemo(() => {
    const raw = liquidityRead.data ? bn(liquidityRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [liquidityRead.data])

  const dailyGlobalLimit = useMemo(() => {
    const raw = dailyGlobalRead.data ? bn(dailyGlobalRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [dailyGlobalRead.data])

  const dailyWalletLimit = useMemo(() => {
    const raw = dailyWalletRead.data ? bn(dailyWalletRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [dailyWalletRead.data])

  const totalGlobalLimit = useMemo(() => {
    const raw = totalGlobalLimitRead.data ? bn(totalGlobalLimitRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [totalGlobalLimitRead.data])

  const totalWalletLimit = useMemo(() => {
    const raw = totalWalletLimitRead.data ? bn(totalWalletLimitRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [totalWalletLimitRead.data])

  const resetPeriod = useMemo(
    () => (resetPeriodRead.data ? Number(resetPeriodRead.data.toString()) : 86400),
    [resetPeriodRead.data],
  )

  const nextResetTime = useMemo(
    () => (nextResetTimeRead.data ? nextResetTimeRead.data.toString() : '0'),
    [nextResetTimeRead.data],
  )

  const isPaused = useMemo(() => !!pausedRead.data, [pausedRead.data])

  const dailySwapped = useMemo(() => {
    const raw = dailySwappedRead.data ? bn(dailySwappedRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [dailySwappedRead.data])

  const globalUsed = useMemo(
    () => totalGlobalLimit.minus(dailyGlobalLimit),
    [totalGlobalLimit, dailyGlobalLimit],
  )

  const walletUsed = useMemo(
    () => (address ? totalWalletLimit.minus(dailyWalletLimit) : bn(0)),
    [totalWalletLimit, dailyWalletLimit, address],
  )

  const swapRate = useMemo(() => {
    if (swapRateRead.data && Array.isArray(swapRateRead.data)) {
      const numerator = bn(swapRateRead.data[0].toString())
      const denominator = bn(swapRateRead.data[1].toString())
      if (denominator.gt(0)) {
        return numerator.div(denominator)
      }
    }
    return bn(0.8)
  }, [swapRateRead.data])

  const amountBN = bn(amount || 0)
  const previewReceive = amountBN.times(swapRate)

  useEffect(() => {
    if (isPaused) return setErrorMessage('Contract is currently paused.')
    if (!amount) return setErrorMessage('')
    if (amountBN.lte(0)) return setErrorMessage('Amount must be greater than zero.')
    if (amountBN.gt(oldBal)) return setErrorMessage('Insufficient balance.')
    if (previewReceive.gt(liquidity)) return setErrorMessage('Not enough liquidity in pool.')
    if (previewReceive.gt(dailyGlobalLimit))
      return setErrorMessage('Exceeds daily global limit.')
    if (previewReceive.gt(dailyWalletLimit))
      return setErrorMessage('Exceeds your daily wallet limit.')
    setErrorMessage('')
  }, [
    amount,
    amountBN,
    oldBal,
    previewReceive,
    liquidity,
    dailyGlobalLimit,
    dailyWalletLimit,
    isPaused,
  ])

  const { config: approveCfg, error: approvePrepareError, isError: isApprovePrepareError } = usePrepareContractWrite({
    address: oldToken,
    abi: erc20Abi,
    functionName: 'approve',
    args: [ipcSwapAdd, MaxUint256],
    enabled: !!address && !errorMessage,
    chainId,
  })

  const { write: approveWrite, data: approveTx, error: approveWriteError, isError: isApproveWriteError } = useContractWrite(approveCfg)
  const {
    isLoading: approving,
    isSuccess: approveSuccess,
    isError: approveError,
  } = useWaitForTransaction({ hash: approveTx?.hash })

  // Log errors for debugging
  useEffect(() => {
    if (isApprovePrepareError && approvePrepareError) {
      console.error('Approve preparation error:', approvePrepareError)
    }
  }, [isApprovePrepareError, approvePrepareError])

  useEffect(() => {
    if (isApproveWriteError && approveWriteError) {
      console.error('Approve write error:', approveWriteError)
    }
  }, [isApproveWriteError, approveWriteError])

  const { config: swapCfg, error: swapPrepareError, isError: isSwapPrepareError } = usePrepareContractWrite({
    address: ipcSwapAdd,
    abi: ipcSwapAbi,
    functionName: 'swapToken',
    args: [parseUnits(amount || '0', 6)],
    enabled: !!address && !!amount && !errorMessage,
    chainId,
  })
  const { write: swapWrite, data: swapTx, error: swapWriteError, isError: isSwapWriteError } = useContractWrite(swapCfg)
  const {
    isLoading: swapping,
    isSuccess: swapSuccess,
    isError: swapError,
  } = useWaitForTransaction({ hash: swapTx?.hash })

  // Log errors for debugging
  useEffect(() => {
    if (isSwapPrepareError && swapPrepareError) {
      console.error('Swap preparation error:', swapPrepareError)
    }
  }, [isSwapPrepareError, swapPrepareError])

  useEffect(() => {
    if (isSwapWriteError && swapWriteError) {
      console.error('Swap write error:', swapWriteError)
    }
  }, [isSwapWriteError, swapWriteError])

  // Show pending notification when approval transaction is submitted
  useEffect(() => {
    if (approveTx?.hash && approving) {
      toast.loading(
        <div className="text-center py-2">
          <div>Approval transaction submitted!</div>
          <div style={{ fontSize: '11px', marginTop: '5px' }}>
            <a
              style={{ color: '#00f6ff' }}
              href={`https://polygonscan.com/tx/${approveTx.hash}`}
              target="_blank"
              rel="noreferrer"
            >
              View on Polygonscan
            </a>
          </div>
          <div style={{ fontSize: '11px', marginTop: '5px' }}>Waiting for confirmation...</div>
        </div>,
        { id: 'approve-tx' }
      )
    }
  }, [approveTx?.hash, approving])

  useEffect(() => {
    if (approveSuccess) {
      toast.dismiss('approve-tx')
      toast.success(
        <div className="text-center py-2">
          Approve Success!
          {approveTx?.hash && (
            <div>
              <a
                style={{ color: '#000' }}
                href={`https://polygonscan.com/tx/${approveTx.hash}`}
                target="_blank"
                rel="noreferrer"
              >
                View on Polygonscan
              </a>
            </div>
          )}
        </div>,
      )
      const t = setTimeout(() => toast.dismiss(), 3000)
      return () => clearTimeout(t)
    }
  }, [approveSuccess, approveTx?.hash])

  useEffect(() => {
    if (approveError) {
      toast.dismiss('approve-tx')
      toast.error(<div className="text-center py-2">Approval failed. Please try again.</div>)
      const t = setTimeout(() => toast.dismiss(), 3000)
      return () => clearTimeout(t)
    }
  }, [approveError])

  // Show pending notification when swap transaction is submitted
  useEffect(() => {
    if (swapTx?.hash && swapping) {
      toast.loading(
        <div className="text-center py-2">
          <div>Swap transaction submitted!</div>
          <div style={{ fontSize: '11px', marginTop: '5px' }}>
            <a
              style={{ color: '#00f6ff' }}
              href={`https://polygonscan.com/tx/${swapTx.hash}`}
              target="_blank"
              rel="noreferrer"
            >
              View on Polygonscan
            </a>
          </div>
          <div style={{ fontSize: '11px', marginTop: '5px' }}>Waiting for confirmation...</div>
        </div>,
        { id: 'swap-tx' }
      )
    }
  }, [swapTx?.hash, swapping])

  useEffect(() => {
    if (swapSuccess) {
      toast.dismiss('swap-tx')
      toast.success(
        <div className="text-center py-2">
          Swap Success!
          {swapTx?.hash && (
            <div>
              <a
                style={{ color: '#000' }}
                href={`https://polygonscan.com/tx/${swapTx.hash}`}
                target="_blank"
                rel="noreferrer"
              >
                View on Polygonscan
              </a>
            </div>
          )}
        </div>,
      )
      setAmount('')
      setDisplayAmount('')
      const t = setTimeout(() => toast.dismiss(), 3000)
      return () => clearTimeout(t)
    }
  }, [swapSuccess, swapTx?.hash])

  useEffect(() => {
    if (swapError) {
      toast.dismiss('swap-tx')
      toast.error(<div className="text-center py-2">Swap failed. Please try again.</div>)
      const t = setTimeout(() => toast.dismiss(), 3000)
      return () => clearTimeout(t)
    }
  }, [swapError])

  return (
    <div className="staking-card-container">
      <div className="staking-card">
        {/* Contract Status Badge */}
        {isPaused && (
          <div
            style={{
              backgroundColor: '#ff4444',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: '6px',
              marginBottom: '15px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            Contract Paused
          </div>
        )}

        {/* Swap Rate */}
        <h4 className="total-stake">Swap Rate</h4>
        <h3 className="amount">5 IP Asset = 4 IPC (IP Classic)</h3>
        <div style={{ fontSize: '14px', color: '#a1a1a1', marginBottom: '20px' }}>
          1 IP Asset ≈ {swapRate.toFixed(6)} IPC
        </div>

        {/* Balances */}
        <div className="wallet-balance">Your IP Asset Balance: {fmt(oldBal)}</div>
        <div className="wallet-balance" style={{ marginBottom: '20px' }}>
          Your IP Classic Balance: {fmt(newBal)}
        </div>

        {/* Liquidity Info */}
        <div
          style={{
            backgroundColor: '#161b22',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
          }}
        >
          <div style={{ fontSize: '12px', color: '#a1a1a1', marginBottom: '5px' }}>
            Contract Liquidity
          </div>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatNumber(liquidity)} IPC</div>
        </div>

        {/* Input Field */}
        <div className="stake-input-container">
          <input
            type="text"
            placeholder="Enter IP Asset amount"
            className="stake-input"
            value={displayAmount}
            onChange={(e) => handleAmountChange(e.target.value)}
            disabled={isPaused}
          />
          <button
            className="max-button"
            onClick={() => {
              const maxValue = oldBal.decimalPlaces(6, BigNumber.ROUND_DOWN).toString()
              handleAmountChange(maxValue)
            }}
            disabled={isPaused}
            style={{ marginRight: '5px' }}
          >
            MAX
          </button>
          <button
            className="max-button"
            onClick={() => {
              setAmount('')
              setDisplayAmount('')
              setErrorMessage('')
            }}
            disabled={isPaused}
            style={{ backgroundColor: '#dc3545' }}
          >
            CLEAR
          </button>
        </div>

        {/* Preview Receive */}
        {amount && !errorMessage && (
          <div
            style={{
              backgroundColor: '#161b22',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '12px',
              marginBottom: '12px',
            }}
          >
            <div style={{ fontSize: '12px', color: '#a1a1a1', marginBottom: '5px' }}>
              You Will Receive
            </div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#00f6ff' }}>
              {fmt(previewReceive)} IPC
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div
            style={{
              color: '#ff4444',
              backgroundColor: '#2a1515',
              padding: '10px',
              borderRadius: '6px',
              marginTop: '12px',
              fontSize: '13px',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* Daily Limits Section */}
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <h4
            style={{
              fontSize: '14px',
              marginBottom: '15px',
              color: '#fff',
              textAlign: 'left',
            }}
          >
            Daily Swap Limits
          </h4>

          {/* Global Limit Progress Bar */}
          <ProgressBar
            current={globalUsed.toNumber()}
            total={totalGlobalLimit.toNumber()}
            label="Global Limit Used (IP Asset)"
            color="#00f6ff"
          />

          {/* Global Limit in IP Asset */}
          <div style={{ fontSize: '11px', color: '#a1a1a1', marginTop: '-10px', marginBottom: '15px', textAlign: 'right' }}>
            Remaining: {formatNumber(dailyGlobalLimit)} IP Asset
          </div>

          {/* Wallet Limit Progress Bar */}
          {address && (
            <>
              <ProgressBar
                current={walletUsed.toNumber()}
                total={totalWalletLimit.toNumber()}
                label="Your Wallet Limit Used (IP Asset)"
                color="#FFD700"
              />
              {/* Wallet Limit in IP Asset */}
              <div style={{ fontSize: '11px', color: '#a1a1a1', marginTop: '-10px', marginBottom: '10px', textAlign: 'right' }}>
                Remaining: {formatNumber(dailyWalletLimit)} IP Asset
              </div>
            </>
          )}
        </div>

        {/* Countdown Timer */}
        {nextResetTime && nextResetTime !== '0' && (
          <div className="lock-period-container">
            <div style={{ fontSize: '14px', color: '#a1a1a1', marginBottom: '10px' }}>
              Time Until Limit Reset
            </div>
            <CountdownTimer targetTimestamp={nextResetTime} />
          </div>
        )}

        {/* Action Buttons */}
        {!address ? (
          <div className="text-center mb-2" style={{ marginTop: 12 }}>
            <ConnectWallet />
          </div>
        ) : (
          <>
            {allowanceHuman.lt(amountBN) && (
              <button
                className="connect-button"
                onClick={() => {
                  if (!approveWrite) {
                    toast.error('Cannot prepare approval transaction. Please check console for details.')
                    return
                  }
                  try {
                    approveWrite()
                  } catch (err) {
                    console.error('Error calling approveWrite:', err)
                    toast.error('Failed to submit approval transaction.')
                  }
                }}
                disabled={!address || !amount || !!errorMessage || approving || isPaused || !approveWrite}
                style={{
                  backgroundColor: buttonColor,
                  marginTop: 12,
                  opacity: (!approveWrite && address && amount && !errorMessage && !isPaused) ? 0.7 : 1,
                  cursor: (!approveWrite && address && amount && !errorMessage && !isPaused) ? 'not-allowed' : 'pointer'
                }}
              >
                {approving ? 'Approving...' : !approveWrite && address && amount && !errorMessage && !isPaused ? 'Preparing...' : 'Approve IP Asset'}
              </button>
            )}

            {allowanceHuman.gte(amountBN) && (
              <button
                className="connect-button"
                onClick={() => {
                  if (!swapWrite) {
                    toast.error('Cannot prepare swap transaction. Please check console for details.')
                    return
                  }
                  try {
                    swapWrite()
                  } catch (err) {
                    console.error('Error calling swapWrite:', err)
                    toast.error('Failed to submit swap transaction.')
                  }
                }}
                disabled={!address || !amount || !!errorMessage || swapping || isPaused || !swapWrite}
                style={{
                  backgroundColor: buttonColor,
                  marginTop: 12,
                  opacity: (!swapWrite && address && amount && !errorMessage && !isPaused) ? 0.7 : 1,
                  cursor: (!swapWrite && address && amount && !errorMessage && !isPaused) ? 'not-allowed' : 'pointer'
                }}
              >
                {swapping ? 'Swapping...' : !swapWrite && address && amount && !errorMessage && !isPaused ? 'Preparing...' : 'Swap to IPC'}
              </button>
            )}
          </>
        )}

        <button
          className="connect-button"
          onClick={async () => {
            try {
              if (window.ethereum) {
                await window.ethereum.request({
                  method: 'wallet_watchAsset',
                  params: {
                    type: 'ERC20',
                    options: {
                      address: newToken,
                      symbol: 'IPC',
                      decimals: 6,
                      image: '',
                    },
                  },
                })
              } else {
                toast.error('Wallet is not available.')
              }
            } catch (err) {
              console.error(err)
              toast.error('Failed to add token to wallet.')
            }
          }}
          disabled={!address}
          style={{ backgroundColor: buttonColor, marginTop: 12 }}
        >
          Add IPC Token to Wallet
        </button>

        {/* Contract Information */}
        <div style={{ marginTop: '20px' }}>
          <div
            style={{
              backgroundColor: '#161b22',
              borderRadius: '8px',
              padding: '15px',
              fontSize: '12px',
            }}
          >
            <div style={{ color: '#a1a1a1', marginBottom: '10px' }}>Contract Information</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#a1a1a1' }}>Status:</span>
              <span style={{ fontWeight: 'bold', color: isPaused ? '#ff4444' : '#4CAF50' }}>
                {isPaused ? 'Paused' : 'Active'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#a1a1a1' }}>Reset Period:</span>
              <span style={{ fontWeight: 'bold' }}>{resetPeriod / 3600} hours</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#a1a1a1' }}>Total Swapped Today:</span>
              <span style={{ fontWeight: 'bold' }}>{formatNumber(dailySwapped)} IP Asset</span>
            </div>
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #2c2f36' }}>
              <div style={{ color: '#a1a1a1', fontSize: '11px', marginBottom: '5px' }}>
                Swap Contract:
              </div>
              <div
                style={{
                  fontSize: '11px',
                  wordBreak: 'break-all',
                  color: '#00f6ff',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  navigator.clipboard.writeText(ipcSwapAdd)
                  toast.success('Address copied!')
                }}
              >
                <a
                  href='https://polygonscan.com/address/0xA0a8F04dcb8E08a60e9f7907FEbeBD3E519F1684#code'
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: 'inherit',
                    textDecoration: 'none'
                  }}
                >
                  {ipcSwapAdd}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Info Text */}
        <div className="staking-info mt-4">
          Enter the amount of IP Asset tokens you want to swap. You'll receive IP Classic (IPC)
          tokens at a 5:4 ratio. Make sure you're within the daily limits.
        </div>
      </div>
    </div>
  )
}

export default IpcSwapCard
